import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateCompanyQueries } from "@/lib/companyQueries";
import { websiteHost } from "@/components/record/recordFormat";
import type { CompanyData } from "../companyTypes";

export type BuyerEnrichPhase = "idle" | "website" | "apollo" | "review" | "done";

/**
 * Website scan (with Apollo) or a one-pager, then a review with per-field
 * checkboxes, then apply. The names below match the code these bodies came
 * from, so they read unchanged.
 */
export function useBuyerEnrichment(company: CompanyData) {
  const queryClient = useQueryClient();
  const [enrichPhase, setEnrichPhase] = useState<BuyerEnrichPhase>("idle");
  const [enrichResult, setEnrichResult] = useState<any>(null);
  const [enrichApplying, setEnrichApplying] = useState(false);
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});
  const id = company.id;
  const c = company.acquisitionCriteria;
  const domain = websiteHost(company.website) || company.enrichmentData?.domain;

  const runEnrich = async () => {
    if (!domain) { toast.error("No website to enrich from"); return; }
    setEnrichPhase("website");
    try {
      const res = await fetch(`/api/companies/${id}/enrich-combined`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });
      const data = await res.json();
      setEnrichResult(data);

      if (data.websiteError && !data.website && !data.apollo) {
        setEnrichPhase("done");
        toast.error(data.websiteError);
        return;
      }
      if (data.websiteError && !data.website && data.apollo) {
        toast(data.websiteError + " Apollo data applied (funding, links).");
        // Auto-apply Apollo metadata since there's no website data to review
        try {
          await fetch(`/api/companies/${id}/apply-enrichment`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fields: {}, domain,
              apolloId: data.apollo.apolloId, fundingHistory: data.apollo.fundingHistory,
              foundedYear: data.apollo.foundedYear, totalFunding: data.apollo.totalFunding,
              phone: data.apollo.phone, linkedinUrl: data.apollo.linkedinUrl,
            }),
          });
          invalidateCompanyQueries(queryClient, id);
        } catch {}
        setEnrichPhase("done");
        return;
      }
      if (data.website || data.apollo) {
        // Pre-select fields: check new fields by default, uncheck overwrite fields
        const w = data.website || {};
        const selections: Record<string, boolean> = {};
        if (w.investmentProfile) selections.investmentProfile = !company?.description;
        if (w.firmType) selections.firmType = !company?.subType;
        if (w.minDealSize || w.maxDealSize) selections.dealSize = !c?.minDealSize;
        if (w.targetIndustries?.length) selections.targetIndustries = !c?.targetIndustries?.length;
        if (w.geoFocus && w.geoFocus !== "national") selections.geoFocus = !c?.geoFocus || c?.geoFocus === "national";
        if (w.investmentThemes?.length) selections.investmentThemes = !c?.investmentThemes?.length;
        if (w.portfolioCompanies?.length) selections.portfolioCompanies = true;
        setSelectedFields(selections);
        setEnrichPhase("review");
      } else {
        setEnrichPhase("done");
        toast("No data found");
      }
    } catch { setEnrichPhase("done"); toast.error("Enrichment failed"); }
  };

  const applyEnrichment = async () => {
    if (!enrichResult) return;
    setEnrichApplying(true);
    try {
      const a = enrichResult.apollo || {};
      const w = enrichResult.website || {};
      const sel = selectedFields;

      // Company-level fields from website (only selected ones)
      const fields: Record<string, any> = {};
      if (sel.investmentProfile && w.investmentProfile) fields.description = w.investmentProfile;
      if (sel.firmType && w.firmType) fields.firmType = w.firmType;

      // Apollo metadata (auto-applied — profile link, funding, phone, linkedin)
      await fetch(`/api/companies/${id}/apply-enrichment`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fields,
          domain,
          apolloId: a.apolloId,
          fundingHistory: a.fundingHistory,
          foundedYear: a.foundedYear,
          totalFunding: a.totalFunding,
          phone: a.phone,
          linkedinUrl: a.linkedinUrl,
        }),
      });

      // Save criteria from website scrape (only selected fields)
      const criteriaUpdates: Record<string, any> = {};
      if (sel.dealSize) { criteriaUpdates.minDealSize = w.minDealSize; criteriaUpdates.maxDealSize = w.maxDealSize; }
      if (sel.targetIndustries) { criteriaUpdates.targetIndustries = w.targetIndustries; criteriaUpdates.industryFocus = w.industryFocus || "focused"; }
      if (sel.geoFocus) { criteriaUpdates.geoFocus = w.geoFocus; criteriaUpdates.primaryGeographies = w.primaryGeographies; }
      if (sel.investmentThemes) { criteriaUpdates.investmentThemes = w.investmentThemes; }

      if (Object.keys(criteriaUpdates).length > 0) {
        await fetch(`/api/companies/${id}/criteria`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({
            ...criteriaUpdates,
            industryFocus: criteriaUpdates.industryFocus ?? c?.industryFocus ?? "opportunistic",
            targetIndustries: criteriaUpdates.targetIndustries ?? c?.targetIndustries ?? [],
            investmentThemes: criteriaUpdates.investmentThemes ?? c?.investmentThemes ?? [],
            minDealSize: criteriaUpdates.minDealSize ?? c?.minDealSize ?? null,
            maxDealSize: criteriaUpdates.maxDealSize ?? c?.maxDealSize ?? null,
            geoFocus: criteriaUpdates.geoFocus ?? c?.geoFocus ?? "national",
            primaryGeographies: criteriaUpdates.primaryGeographies ?? c?.primaryGeographies ?? [],
          }),
        });
      }

      // Save portfolio companies (only if selected)
      if (sel.portfolioCompanies && w.portfolioCompanies?.length) {
        for (const pc of w.portfolioCompanies) {
          await fetch(`/api/companies/${id}/past-acquisitions`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ acquiredCompanyName: pc.name, industry: pc.industry, closedDate: pc.year ? `${pc.year}-01-01` : null }),
          }).catch(() => {});
        }
      }

      invalidateCompanyQueries(queryClient, id);
      queryClient.invalidateQueries({ queryKey: ["past-acquisitions", id] });
      toast.success("Enrichment applied");
      setEnrichPhase("done");
    } catch { toast.error("Failed to apply"); }
    finally { setEnrichApplying(false); }
  };

  /** The one-pager input's handler, given the file instead of the change event. */
  const readOnePager = async (file: File) => {
              setEnrichPhase("website");
              try {
                const fd = new FormData(); fd.append("file", file);
                const res = await fetch("/api/extract-one-pager", { method: "POST", credentials: "include", body: fd });
                const data = await res.json();
                if (!res.ok) { toast.error(data.error || "Failed to parse"); setEnrichPhase("done"); return; }
                // Map one-pager result to same format as website enrichment
                const ic = data.investmentCriteria || {};
                const geo = (ic.geography || "").toLowerCase();
                const isNational = !geo || geo.includes("national");
                const fmtSize = (m: string) => { const n = parseFloat(m); if (!n) return null; return n >= 1000 ? `$${(n / 1000).toFixed(0)}B` : `$${n}M`; };
                setEnrichResult({
                  website: {
                    investmentProfile: data.company?.description || null,
                    firmType: data.company?.firmType || null,
                    industryFocus: ic.targetIndustries?.length ? "focused" : "opportunistic",
                    targetIndustries: ic.targetIndustries || null,
                    investmentThemes: ic.transactionTypes || null,
                    minDealSize: fmtSize(ic.targetRevenueLow) || null,
                    maxDealSize: fmtSize(ic.targetRevenueHigh) || null,
                    geoFocus: isNational ? "national" : "focused",
                    primaryGeographies: !isNational && ic.geography ? [ic.geography] : null,
                    portfolioCompanies: data.portfolioCompanies || null,
                    confidence: {},
                  },
                  apollo: null, current: { description: company?.description, subType: company?.subType },
                });
                const w = { investmentProfile: data.company?.description, firmType: data.company?.firmType, minDealSize: fmtSize(ic.targetRevenueLow), targetIndustries: ic.targetIndustries, investmentThemes: ic.transactionTypes, geoFocus: isNational ? null : "focused", portfolioCompanies: data.portfolioCompanies };
                const sel: Record<string, boolean> = {};
                if (w.investmentProfile) sel.investmentProfile = !company?.description;
                if (w.firmType) sel.firmType = !company?.subType;
                if (w.minDealSize) sel.dealSize = !c?.minDealSize;
                if (w.targetIndustries?.length) sel.targetIndustries = !c?.targetIndustries?.length;
                if (w.investmentThemes?.length) sel.investmentThemes = !c?.investmentThemes?.length;
                if (w.portfolioCompanies?.length) sel.portfolioCompanies = true;
                setSelectedFields(sel);
                setEnrichPhase("review");
              } catch { toast.error("Failed to parse one-pager"); setEnrichPhase("done"); }
  };

  return {
    phase: enrichPhase, setPhase: setEnrichPhase, result: enrichResult, applying: enrichApplying,
    selected: selectedFields, setSelected: setSelectedFields, runEnrich, readOnePager, apply: applyEnrichment,
  };
}

export type BuyerEnrichment = ReturnType<typeof useBuyerEnrichment>;
